import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { KnownDateFilterTimeFrame } from "@redotech/redo-views-common/advanced-filters/date-filter";
import {
  AdvancedFilterType,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { AdvancedTableFilter } from "../advanced-filter";

/**
 * Whether a pending filter has a concrete value and is ready to become a pill.
 * Used to gate pill creation in the deferred-pill add-filter flow.
 */
export function isAdvancedFilterValued(data: AdvancedFilterData): boolean {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (data.type) {
    case AdvancedFilterType.DATE:
      if (data.value == null) {
        return false;
      }
      // "Custom"/"Relative" aren't real values until their sub-picker is filled.
      if (data.value === KnownDateFilterTimeFrame.CUSTOM) {
        return data.customDate != null;
      }
      if (data.value === KnownDateFilterTimeFrame.RELATIVE) {
        return data.relativeTime != null;
      }
      return true;
    case AdvancedFilterType.ARRAY_TO_STRING:
      return (
        Array.isArray(data.value) &&
        data.value.some((v) => v != null && v !== "")
      );
    case AdvancedFilterType.EXISTS:
      return data.value != null;
    case AdvancedFilterType.BOOLEAN:
      return data.value != null;
    default:
      return false;
  }
}

/**
 * The outcome of a single value change in the deferred-pill add-filter flow.
 *
 * - `none`: nothing has a concrete value yet, so no pill exists.
 * - `add`: the first concrete value — create the pill.
 * - `update`: the pill already exists, so replace `previous` in place instead
 *   of spawning a duplicate (this is what keeps multi-select working).
 */
export type FilterCommitPlan =
  | { action: "none" }
  | { action: "add"; committed: AdvancedFilterData }
  | {
      action: "update";
      previous: AdvancedFilterData;
      committed: AdvancedFilterData;
    };

/**
 * Decide what to do when a pending filter's value changes.
 *
 * @param committed The filter already committed as a pill, or `null` if the
 *   pending filter has never produced a concrete value yet. `previous` in the
 *   returned `update` plan is this exact reference so callers can match the
 *   existing clause by identity.
 * @param data The latest pending filter data.
 */
/**
 * Applies a deferred-pill update to a filter list by matching the pill to
 * replace by `data.name`, not reference identity. Name-based matching is
 * required because callers such as Table2 call `setAdvancedFilterParams` after
 * committing a pill, which triggers a `decodeAdvancedFilters` useEffect that
 * recreates all data objects — breaking `===` identity even though the values
 * are unchanged. The LAST match wins, mirroring `findDeferredClauseIndex`: the
 * cascade appends, so a second pill for an already-filtered field is the one
 * being edited and the saved one must be left alone.
 */
export function applyDeferredFilterUpdate(
  filters: AdvancedTableFilter[],
  previous: AdvancedFilterData,
  buildReplacement: (existing: AdvancedTableFilter) => AdvancedTableFilter,
): AdvancedTableFilter[] {
  let target = -1;
  for (let i = filters.length - 1; i >= 0; i--) {
    if (filters[i]?.data.name === previous.name) {
      target = i;
      break;
    }
  }
  if (target === -1) {
    return filters;
  }
  const copy = [...filters];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  copy[target] = buildReplacement(copy[target]!);
  return copy;
}

/**
 * Index of the top-level clause a deferred pill edit should replace, or -1.
 * Nested groups are skipped and the LAST same-named clause wins.
 */
export function findDeferredClauseIndex(
  group: FilterGroup,
  name: string,
): number {
  for (let i = group.clauses.length - 1; i >= 0; i--) {
    const clause = group.clauses[i];
    if (clause && !isFilterGroup(clause) && clause.name === name) {
      return i;
    }
  }
  return -1;
}

/**
 * `applyDeferredFilterUpdate` for nested `FilterGroup` storage. Name-matched
 * for the same reason; a pill removed mid-edit leaves the group untouched.
 */
export function applyDeferredFilterUpdateToGroup(
  group: FilterGroup,
  previous: AdvancedFilterData,
  next: AdvancedFilterData,
): FilterGroup {
  const index = findDeferredClauseIndex(group, previous.name);
  if (index === -1) {
    return group;
  }
  const clauses = [...group.clauses];
  clauses[index] = next;
  return { ...group, clauses };
}

export function planFilterCommit(
  committed: AdvancedFilterData | null,
  data: AdvancedFilterData,
): FilterCommitPlan {
  // Once a pill exists, every edit updates that same clause — even an edit that
  // clears the value — so multi-select never creates duplicate pills.
  if (committed != null) {
    return { action: "update", previous: committed, committed: data };
  }
  if (isAdvancedFilterValued(data)) {
    return { action: "add", committed: data };
  }
  return { action: "none" };
}
